import { useState } from 'react';
import type { ApiClient } from '../../api/client';
import type { ComplimentaryCareEnrollment } from '../../api/types';

export type ReviewAction = 'approve' | 'reject' | 'deactivate' | 'reactivate';

/**
 * Whether to ask before doing it. Rejecting and deactivating take something away, so they always ask.
 * So does approving a proposed change — it is the one approval that is not purely additive: the
 * enrollment it replaces stops being active and is not kept.
 */
function needsConfirmation(action: ReviewAction, enrollment: ComplimentaryCareEnrollment): boolean {
  switch (action) {
    case 'reject':
    case 'deactivate':
      return true;
    case 'approve':
      return enrollment.isProposedEdit;
    case 'reactivate':
      return false;
  }
}

/**
 * The HR decisions, shared by the list and the detail page so both behave identically — same
 * confirmation rules, same required rejection reason, same error handling.
 *
 * `onChanged` re-reads rather than being handed the updated row, because approving a proposed change
 * also removes the row it replaces — a decision can change more than the record it was made on.
 */
export function useReviewActions(api: ApiClient, onChanged: () => void | Promise<void>) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<{ enrollment: ComplimentaryCareEnrollment; action: ReviewAction } | null>(null);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);

  async function run(enrollment: ComplimentaryCareEnrollment, action: ReviewAction, actionNote?: string) {
    setError(null);
    setBusyId(enrollment.id);

    try {
      await api.reviewComplimentaryCareEnrollment(enrollment.id, action, actionNote);
      await onChanged();
      setConfirming(null);
      setNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The action failed.');
    } finally {
      setBusyId(null);
    }
  }

  function request(enrollment: ComplimentaryCareEnrollment, action: ReviewAction) {
    if (needsConfirmation(action, enrollment)) {
      setNote('');
      setNoteError(null);
      setConfirming({ enrollment, action });
      return;
    }

    void run(enrollment, action);
  }

  function confirm() {
    if (!confirming) return;

    // A rejection the employee cannot act on is a dead end, so the reason is not optional.
    if (confirming.action === 'reject' && note.trim().length === 0) {
      setNoteError('Say why it was rejected — the employee sees this.');
      return;
    }

    void run(confirming.enrollment, confirming.action, note.trim() || undefined);
  }

  return {
    busyId,
    error,
    confirming,
    note,
    noteError,
    setNote,
    request,
    confirm,
    cancel: () => setConfirming(null),
  };
}
